import AppErrorBoundary from "components/Common/AppErrorBoundary";
import { useTranslationsQuery } from "api/api-misc";
import {
  useFetchIncomlendPermissionsQuery,
  useFetchUserPermissionsQuery,
  useGetCurrentUserQuery,
} from "api/api-users-v2";
import { INCOMLEND_INTERNAL, INCOMLEND_INTERNAL_LINK } from "codes";
import isEqual from "fast-deep-equal";
import MainContentLoader, {
  DashboardContentLoadingContext,
} from "components/Common/MainContentLoader";
import Dashboard from "components/Dashboard";
import EmailVerificationGate from "components/Login/EmailVerificationGate";
import GtcApprovalModal from "components/GTC/Approval";
import NewVersionPrompt from "components/Dashboard/NewVersionPrompt";
import Floater from "components/Floater";
import auth from "helpers/auth";
import history from "helpers/history";
import offlineI18n from "helpers/i18n";
import onlineI18n from "helpers/i18n-online";
import { isInvestorAccessUser } from "helpers/investorAccess";
import { isInternalRole } from "helpers/normalizeRoleName";
import { roleRedirect } from "helpers/redirect";
import tokenValidate from "helpers/tokenValidate";
import {
  isActingAsClientAccount,
  isInternalViewActive,
} from "helpers/viewContext";
import useIsMobile from "hooks/useIsMobile";
import { Suspense, useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  setAppPermission,
  setAppPermissions,
  setRole,
  setUserPermission,
} from "redux/reducers/appStatesReducer";
import InvestorAccessPage from "pages/investor-access";
import { UnsavedWorkProvider } from "hooks/UnsavedWorkContext";

const shouldSkipClientPermissions = (
  hasUser: boolean,
  userRole: string | undefined,
  actingAsClientAccount: boolean,
) => !hasUser || (isInternalRole(userRole) && !actingAsClientAccount);

const Incomlend = () => {
  auth();
  const dispatch = useAppDispatch();
  const [i18n, seti18n] = useState(offlineI18n);
  const isTokenValid = tokenValidate();
  const {
    data: user,
    isFetching: isFetchingCurrentUser,
    isError: isCurrentUserError,
    refetch: refetchCurrentUser,
  } = useGetCurrentUserQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !isTokenValid.loggedIn,
  });
  const { data: translations, isSuccess } = useTranslationsQuery(undefined, {
    skip: !isTokenValid.loggedIn,
  });

  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const APP_PERMISSIONS = useAppSelector((state) => state.appState.permissions);
  const APP_STATE = useAppSelector((state) => state.appState.value);
  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const hasSavedDashboardContext = Boolean(USER_ROLE?.id && APP_PERMISSION);
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission,
  );
  const actingAsClientAccount = isActingAsClientAccount({
    appPermission: APP_PERMISSION,
    appState: APP_STATE,
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    const language = new URLSearchParams(url.search)?.get("lang");
    if (language) i18n.changeLanguage(language);
  }, []);

  const isMobile = useIsMobile();
  useEffect(() => {
    if (isSuccess && translations?.data) seti18n(onlineI18n(translations.data));
  }, [isSuccess, translations]);
  const shouldShowFloater = APP_PERMISSION !== INCOMLEND_INTERNAL;

  const {
    data: userPermissions,
    isFetching: isFetchingUserPermissions,
    isUninitialized: isUserPermissionUninitialized,
  } = useFetchUserPermissionsQuery(user?.id ?? "", {
    skip: shouldSkipClientPermissions(
      Boolean(user),
      user?.user_role,
      actingAsClientAccount,
    ),
  });
  const {
    data: incomlendPermissions,
    isFetching: isFetchingIncomlendPermissions,
    isUninitialized: isIncomlendPermissionUninitialized,
  } = useFetchIncomlendPermissionsQuery(user?.id ?? "", {
    skip: !Boolean(user),
  });

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isMainContentLoading =
    (isFetchingCurrentUser && !user) ||
    (user?.email_verified === true &&
      isInitialLoading &&
      !hasSavedDashboardContext &&
      (isFetchingUserPermissions || isFetchingIncomlendPermissions));

  const isInternalUser =
    isInternalRole(user?.user_role) ||
    Boolean(incomlendPermissions?.groups?.length);

  useEffect(() => {
    if (!isInternalUser || isFetchingIncomlendPermissions) {
      return;
    }

    if (actingAsClientAccount) {
      return;
    }

    const clientOnlyPaths = ["/", "/dashboard"];
    if (
      clientOnlyPaths.includes(window.location.pathname) &&
      isInternalViewActive({
        appPermission: APP_PERMISSION,
        appState: APP_STATE,
      })
    ) {
      history.replace(INCOMLEND_INTERNAL_LINK);
    }
  }, [
    isInternalUser,
    isFetchingIncomlendPermissions,
    APP_PERMISSION,
    APP_STATE,
    actingAsClientAccount,
  ]);

  useEffect(() => {
    if (
      user &&
      !isFetchingCurrentUser &&
      !isFetchingUserPermissions &&
      !isFetchingIncomlendPermissions
    ) {
      const isInternalRoleSelected =
        isInternalUser ||
        APP_PERMISSION === INCOMLEND_INTERNAL ||
        USER_ROLE?.id === INCOMLEND_INTERNAL;
      const actingAsClientAccount = isActingAsClientAccount({
        appPermission: APP_PERMISSION,
        appState: APP_STATE,
      });
      const syncPermissions = (
        nextAppPermissions: string[],
        nextUserPermissions: string[],
      ) => {
        if (!isEqual(APP_PERMISSIONS, nextAppPermissions)) {
          dispatch(setAppPermissions(nextAppPermissions));
        }
        if (!isEqual(USER_PERMISSIONS, nextUserPermissions)) {
          dispatch(setUserPermission(nextUserPermissions));
        }
      };

      //Initial Role creation
      if (USER_ROLE?.id === "" && incomlendPermissions?.groups?.[0]) {
        dispatch(
          setRole({
            id: INCOMLEND_INTERNAL,
            name: INCOMLEND_INTERNAL,
            permission: incomlendPermissions.permissions,
            role: INCOMLEND_INTERNAL,
          }),
        );
        if (!APP_PERMISSION) dispatch(setAppPermission(INCOMLEND_INTERNAL));
        dispatch(setAppPermissions(incomlendPermissions?.groups));
        dispatch(setUserPermission(incomlendPermissions?.permissions));
      } else if (USER_ROLE?.id === "" && userPermissions?.[0]) {
        dispatch(
          setRole({
            ...userPermissions?.[0],
            role: userPermissions?.[0].director ? "Director" : "Non-Director",
          }),
        );
        if (!APP_PERMISSION)
          dispatch(setAppPermission(userPermissions?.[0]?.id));
        dispatch(setAppPermissions(userPermissions?.[0].permission));
        dispatch(setUserPermission(userPermissions?.[0].permission));
      }

      // Keep persisted permission state in sync with fresh backend data.
      // Without this, newly added permissions stay hidden until app state is cleared.
      if (
        isInternalRoleSelected &&
        incomlendPermissions &&
        !actingAsClientAccount
      ) {
        const groups = incomlendPermissions.groups ?? [];
        const permissions = incomlendPermissions.permissions ?? [];
        syncPermissions(groups, permissions);
      } else if (USER_ROLE?.id && userPermissions?.length) {
        const selectedRole =
          userPermissions.find((role) => role.id === USER_ROLE.id) ??
          userPermissions[0];

        if (selectedRole) {
          const permissions = selectedRole.permission ?? [];
          syncPermissions(permissions, permissions);
        }
      }

      //Initial Re-redirection
      const role = USER_ROLE?.id;
      if (
        isUserPermissionUninitialized &&
        isIncomlendPermissionUninitialized &&
        !!!role
      )
        roleRedirect(APP_PERMISSION);

      if (
        isInternalUser &&
        incomlendPermissions?.groups?.length &&
        !actingAsClientAccount
      ) {
        if (APP_PERMISSION !== INCOMLEND_INTERNAL) {
          dispatch(setAppPermission(INCOMLEND_INTERNAL));
        }
      }

      // Keep loader until internal menus are ready (avoids client onboarding flash)
      const internalReady =
        !isInternalUser || Boolean(incomlendPermissions?.groups?.length);

      if (internalReady) {
        setIsInitialLoading(false);
      }
    }
  }, [
    APP_PERMISSION,
    APP_PERMISSIONS,
    APP_STATE,
    actingAsClientAccount,
    USER_ROLE,
    USER_PERMISSIONS,
    user,
    incomlendPermissions,
    isFetchingCurrentUser,
    userPermissions,
    dispatch,
    isFetchingUserPermissions,
    isFetchingIncomlendPermissions,
    isUserPermissionUninitialized,
    isIncomlendPermissionUninitialized,
    isInternalUser,
  ]);

  //Websockets listener
  // useWSS(import.meta.env.VITE_WSS_URL);

  return (
    <AppErrorBoundary scope="Dashboard routes">
      <UnsavedWorkProvider>
        <I18nextProvider i18n={i18n}>
          <EmailVerificationGate
            user={user}
            isUserError={isCurrentUserError}
            isLoggedIn={isTokenValid.loggedIn}
            refreshCurrentUser={() => refetchCurrentUser().unwrap()}
          >
            {isInvestorAccessUser(user) ? (
              <InvestorAccessPage />
            ) : (
              <Dashboard>
                {isMainContentLoading ? (
                  <MainContentLoader />
                ) : (
                  <>
                    <GtcApprovalModal />
                    <DashboardContentLoadingContext.Provider value>
                      <Suspense fallback={<MainContentLoader />}>
                        <Outlet />
                      </Suspense>
                    </DashboardContentLoadingContext.Provider>
                    {!isMobile && (
                      <Floater
                        shouldShowFloater={shouldShowFloater}
                        user={{
                          name: `${user?.first_name} ${user?.last_name}`,
                          email: user?.email,
                        }}
                      />
                    )}
                    <NewVersionPrompt />
                  </>
                )}
              </Dashboard>
            )}
          </EmailVerificationGate>
        </I18nextProvider>
      </UnsavedWorkProvider>
    </AppErrorBoundary>
  );
};

export default Incomlend;
